import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { registraNavigazione } from "../utils/cronologia";

/**
 * Registro della cronologia interna e posizione di scroll per voce.
 *
 * Non renderizza niente: sta una volta sola in `App`, dentro il router, e a
 * ogni cambio di location fa tre cose.
 *
 * 1. Registra la voce (vedi `utils/cronologia.ts`), così i pulsanti "Indietro"
 *    interni sanno se la pagina precedente è quella che promettono.
 * 2. Aprendo una pagina nuova (PUSH su un altro percorso) parte dall'alto. Con
 *    `<BrowserRouter>` niente lo fa da solo: si apriva il dettaglio di un
 *    dispositivo alla stessa altezza a cui si era lasciato l'elenco.
 * 3. Tornando indietro (POP) rimette lo scroll dov'era. Il ripristino del
 *    browser non basta: scatta subito, quando l'elenco non è ancora arrivato
 *    dall'API e la pagina è alta quanto lo scheletro di caricamento, e si
 *    ferma lì. Qui invece si aspetta che la pagina torni abbastanza alta, e si
 *    riapplica la posizione finché il contenuto si assesta — un riquadro
 *    caricato dopo l'elenco (il riepilogo dei Dispositivi) altrimenti la
 *    sposterebbe di nuovo.
 *
 * Il ripristino si interrompe appena l'utente scorre da sé: una pagina che
 * salta mentre la si sta già usando è peggio di una che non ripristina.
 *
 * Ogni voce nasce con una posizione: 0 per una pagina nuova, quella attuale
 * per un pannello aperto sulla stessa pagina o per la prima voce caricata. Se
 * la posizione arrivasse solo dagli eventi di scroll, una pagina mai scorsa non
 * ne avrebbe una, e tornandoci con Indietro resterebbe lo scroll della pagina
 * di dettaglio da cui si torna.
 *
 * Scorre la finestra, non `<main>`: il layout è `min-h-screen` e il contenuto
 * allunga il documento.
 */

const CHIAVE = "winkonsupport_scroll";
/** Oltre questo tempo si smette di aspettare il contenuto. */
const ATTESA_MASSIMA_MS = 5000;
/** Senza cambi di altezza per tanto così, la pagina si considera assestata. */
const ASSESTAMENTO_MS = 600;

function leggiPosizioni(): Record<string, number> {
  try {
    const grezzo = sessionStorage.getItem(CHIAVE);
    const parsed: unknown = grezzo ? JSON.parse(grezzo) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, number>) : {};
  } catch {
    return {};
  }
}

function salvaPosizioni(posizioni: Record<string, number>) {
  try {
    // Solo le ultime: le chiavi di voci ormai lontane non servono più.
    const voci = Object.entries(posizioni).slice(-100);
    sessionStorage.setItem(CHIAVE, JSON.stringify(Object.fromEntries(voci)));
  } catch {
    // Storage negato: lo scroll si ripristina finché la scheda resta aperta.
  }
}

export function CronologiaNavigazione() {
  const location = useLocation();
  const tipo = useNavigationType();

  const posizioni = useRef<Record<string, number>>(leggiPosizioni());
  const chiaveCorrente = useRef(location.key);
  const percorsoPrecedente = useRef<string | null>(null);
  /** Funzione che annulla il ripristino in corso, se ce n'è uno. */
  const annullaRipristino = useRef<(() => void) | null>(null);

  // Il browser, con "auto", ripristinerebbe per conto suo e in anticipo,
  // contendendosi la posizione con quello qui sotto.
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
  }, []);

  // La posizione si annota a ogni scroll, non al cambio di pagina: quando
  // l'effetto del cambio gira il nuovo contenuto è già nel DOM, e la finestra
  // può essere già stata accorciata (e lo scroll troncato) da quello.
  useEffect(() => {
    function annota() {
      if (annullaRipristino.current) return;
      posizioni.current[chiaveCorrente.current] = window.scrollY;
    }
    function salvaTutto() {
      salvaPosizioni(posizioni.current);
    }
    window.addEventListener("scroll", annota, { passive: true });
    window.addEventListener("pagehide", salvaTutto);
    return () => {
      window.removeEventListener("scroll", annota);
      window.removeEventListener("pagehide", salvaTutto);
    };
  }, []);

  useLayoutEffect(() => {
    registraNavigazione(location, tipo);

    annullaRipristino.current?.();
    const chiavePrecedente = chiaveCorrente.current;
    chiaveCorrente.current = location.key;
    // Un REPLACE (il filtro appena digitato) crea una voce nuova al posto della
    // vecchia: la posizione segue la voce, o al ritorno non ci sarebbe niente
    // da ripristinare.
    if (tipo === "REPLACE" && posizioni.current[location.key] === undefined) {
      posizioni.current[location.key] = posizioni.current[chiavePrecedente] ?? window.scrollY;
    }
    const primaVoce = percorsoPrecedente.current === null;
    const cambiaPercorso = !primaVoce && percorsoPrecedente.current !== location.pathname;
    percorsoPrecedente.current = location.pathname;
    // La posizione iniziale della voce, se non ne ha già una (una voce
    // ricaricata la ritrova da sessionStorage).
    const nuovaVoce = posizioni.current[location.key] === undefined && (tipo === "PUSH" || primaVoce);
    if (nuovaVoce) posizioni.current[location.key] = tipo === "PUSH" && cambiaPercorso ? 0 : window.scrollY;
    salvaPosizioni(posizioni.current);

    if (tipo === "PUSH") {
      // Un pannello aperto sulla stessa pagina (?open=) non deve far saltare
      // l'elenco sotto di lui.
      if (cambiaPercorso) window.scrollTo(0, 0);
      return;
    }
    // Una prima voce senza posizione salvata non ha niente da ripristinare.
    if (tipo !== "POP" || nuovaVoce) return;

    const obiettivo = posizioni.current[location.key];
    if (obiettivo === undefined) return;

    const inizio = Date.now();
    let ultimoCambio = Date.now();
    let altezza = -1;
    let raggiunto = false;
    let frame = 0;

    // A ogni frame e non con un ResizeObserver: `html`, `body` e `#root` sono
    // alti il 100% della finestra (index.css), quindi la loro dimensione non
    // cambia quando arriva l'elenco — cambia solo l'altezza scorribile.
    function prova() {
      const ora = Date.now();
      const h = document.documentElement.scrollHeight;
      if (h !== altezza) {
        altezza = h;
        ultimoCambio = ora;
      }
      if (h - window.innerHeight >= obiettivo - 1) {
        if (Math.abs(window.scrollY - obiettivo) > 1) window.scrollTo(0, obiettivo);
        raggiunto = true;
      }
      if (ora - inizio > ATTESA_MASSIMA_MS || (raggiunto && ora - ultimoCambio > ASSESTAMENTO_MS)) {
        fine();
        return;
      }
      frame = requestAnimationFrame(prova);
    }

    // L'utente che scorre da sé vince sul ripristino.
    const eventi = ["wheel", "touchstart", "keydown", "mousedown"] as const;
    function fine() {
      cancelAnimationFrame(frame);
      for (const e of eventi) window.removeEventListener(e, fine);
      if (annullaRipristino.current === fine) annullaRipristino.current = null;
    }
    for (const e of eventi) window.addEventListener(e, fine, { passive: true });
    annullaRipristino.current = fine;

    prova();
    // `location` intera e non solo la key: il registro vuole anche percorso e
    // query aggiornati, che sulla stessa key non cambiano comunque.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return null;
}
